import axios from 'axios';
import { toast } from 'sonner';
import { authService } from '../services/auth.service';
import { getSecureItem, removeSecureItem } from '../utils/localStorage';
import { isTokenExpired } from '../utils/jwt';
// import { toast } from '../components/ui/toaster';

// Get backend URL from environment variable
const baseURL = import.meta.env.VITE_API_BASE_URL;

const axiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to redirect to login page
const redirectToLogin = () => {
  // Clear all auth data
  removeSecureItem('accessToken');
  removeSecureItem('refreshToken');

  // Clear auth store
  authService.logout();

  // Redirect to login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Skip token check for auth endpoints
    if (config.url === '/auth/refresh' || config.url === '/auth/login') {
      return config;
    }

    const accessToken = getSecureItem('accessToken');
    console.log('Making request to:', config.url);
    console.log('Access token exists:', !!accessToken);
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      console.warn('No access token found for request to:', config.url);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {

      // Skip retry for auth endpoints
      if (originalRequest.url === '/auth/refresh' || originalRequest.url === '/auth/login') {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Check if refresh token is expired
        const refreshToken = getSecureItem('refreshToken');

        if (!refreshToken || isTokenExpired(refreshToken)) {
          redirectToLogin();
          return Promise.reject(new Error('Authentication required'));
        }

        // Attempt to refresh the token
        const { accessToken } = await authService.refreshToken();

        // Update default headers
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Process queued requests
        processQueue(null, accessToken);

        // Retry the original request
        return axiosInstance(originalRequest);
      } catch (refreshError: any) {

        // Process queued requests with error
        processQueue(refreshError, null);

        // Clear tokens and redirect to login
        redirectToLogin();

        return Promise.reject(new Error('Authentication required'));
      } finally {
        isRefreshing = false;
      }
    }

    // Global error message handling
    if (error.response?.status !== 401) {
      const status = error.response?.status;
      const data = error.response?.data;
      const message = data?.message || data?.error || error.message || "An unexpected error occurred";

      if (status === 409) {
        toast.error("Duplicate Record", {
          description: message || "A record with this information already exists."
        });
      } else if (status === 400) {
        toast.error("Invalid Request", {
          description: message
        });
      } else if (status === 403) {
        toast.error("Permission Denied", {
          description: "You do not have permission to perform this action."
        });
      } else {
        toast.error("Error", {
          description: message
        });
      }
    }

    return Promise.reject(error);
  }
);

// Export both the instance and the base URL for other files to use
export { baseURL };
export default axiosInstance; 
